import * as Select from '@radix-ui/react-select';
import { Loader2, ChevronDown, RefreshCw, AlertCircle } from 'lucide-react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  useToast,
} from '@/components/ui';
import { APP_CONFIG, ROUTES } from '@/constants';
import {
  useBootstrapConfigQuery,
  useSendOTPMutation,
  useCarriersQuery,
  usePlantsQuery,
} from '@/hooks/auth';
import {
  useAppDispatch,
  useAppSelector,
  setMobileNumber,
  setCountryCode,
  resetOTPFlow,
} from '@/store';
import { cn } from '@/utils/cn';

const APP_VERSION = `V-${APP_CONFIG.version}`;

// ─────────────────────────────────────────────────────────────────
// Niagara Logo Component - Uses actual SVG from Xamarin
// ─────────────────────────────────────────────────────────────────

function NiagaraLogo({ className }: { className?: string }) {
  return <img src="/images/logo_niagara.svg" alt="Niagara" className={cn('h-10', className)} />;
}

// ─────────────────────────────────────────────────────────────────
// Bootstrap Loader Component
// ─────────────────────────────────────────────────────────────────

function BootstrapLoader() {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Full-screen truck splash
  if (showSplash) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-8 bg-[#094887]">
        <img src="/images/SplashScreen.scale-100.png" alt="YMS Truck" />
      </div>
    );
  }

  // Loader after splash
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center"
      style={{ background: 'linear-gradient(to right, #003366 10%, #0B1B25 100%)' }}
    >
      <NiagaraLogo className="mb-8" />
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-white" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Maintenance Mode Component
// ─────────────────────────────────────────────────────────────────

interface MaintenanceModeProps {
  message: string | null;
}

function MaintenanceMode({ message }: MaintenanceModeProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-warning-50 to-warning-100 p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader className="space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-warning/10">
            <AlertCircle className="h-8 w-8 text-warning" />
          </div>
          <CardTitle className="text-2xl">{t('maintenance.title')}</CardTitle>
          <CardDescription className="text-base">
            {message ?? t('maintenance.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('maintenance.tryagain')}</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Bootstrap Error Component
// ─────────────────────────────────────────────────────────────────

interface BootstrapErrorProps {
  error: string;
  isRetrying: boolean;
}

function BootstrapError({ error: _error, isRetrying }: BootstrapErrorProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-destructive/5 to-destructive/10 p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader className="space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl">{t('common.error')}</CardTitle>
          {/* VULN-14: Show user-friendly error, never raw API errors */}
          <CardDescription className="text-base">{t('errors.generic')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button className="w-full" isLoading={isRetrying} loadingText={t('common.retrying')}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

interface CountryCodeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

function CountryCodeSelector({ value, onChange, disabled }: CountryCodeSelectorProps) {
  const { countryCodes } = useAppSelector((state) => state.global);
  // Use country codes strictly from config settings.COUNTRY_CODES
  const options = useMemo(
    () =>
      countryCodes.map((cc) => ({
        value: cc.code,
        shortCode: cc.short_code,
      })),
    [countryCodes]
  );

  // If value doesn't match any option, default to first option
  const matchedOption = options.find((opt) => opt.value === value);
  const currentOption = matchedOption ?? options[0];

  // Sync back to parent if value didn't match any option
  useEffect(() => {
    if (!matchedOption && currentOption) {
      onChange(currentOption.value);
    }
  }, [matchedOption, currentOption, onChange]);

  return (
    <Select.Root
      value={currentOption?.value ?? value}
      onValueChange={onChange}
      disabled={disabled ?? false}
    >
      <Select.Trigger
        className="flex h-[50px] items-center gap-0 md:gap-2 bg-white px-2 md:px-3 text-sm md:text-base whitespace-nowrap shrink-0 border-0 rounded-l-lg focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:!hidden [&_svg]:md:!block"
        aria-label="Country code"
      >
        <Select.Value>
          <span className="text-gray-700 font-normal text-[14px] md:text-base">
            {currentOption?.value} ({currentOption?.shortCode})
          </span>
        </Select.Value>
        <span className="hidden md:inline-flex ml-2 md:ml-4">
          <ChevronDown className="h-4 w-4 text-gray-500" />
        </span>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className="font-poppins z-50 min-w-44 overflow-hidden border-[0.8px] border-gray-300 bg-[#f2f2f2] text-gray-800 shadow-2xl"
          position="popper"
          sideOffset={8}
        >
          <Select.Viewport className="py-1.5">
            {options.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                className={cn(
                  'relative flex cursor-pointer select-none items-center  py-2 px-2 text-sm outline-none',
                  'hover:bg-[#d9d9d9]',
                  'data-[state=checked]:bg-[#91c1e6]',
                  'data-[state=checked]:hover:bg-[#5ca4db]',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                )}
              >
                <Select.ItemText>
                  <span className="font-extralight text-base">
                    {option.value} ({option.shortCode})
                  </span>
                </Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

// ─────────────────────────────────────────────────────────────────
// Login Form Component
// ─────────────────────────────────────────────────────────────────

function LoginForm() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { settings } = useAppSelector((state) => state.global);
  const { mobileNumber, countryCode } = useAppSelector((state) => state.user);
  // Reset mobile number and country code when returning to login
  useEffect(() => {
    dispatch(resetOTPFlow());
  }, [dispatch]);

  const sendOTPMutation = useSendOTPMutation();
  const isSending = sendOTPMutation.isPending;
  const sendError = sendOTPMutation.error
    ? sendOTPMutation.error instanceof Error
      ? sendOTPMutation.error.message
      : t('login.failedToSendOTP')
    : null;

  const handleMobileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Only allow digits
      const value = e.target.value.replace(/\D/g, '');
      dispatch(setMobileNumber(value));
      sendOTPMutation.reset();
    },
    [dispatch, sendOTPMutation]
  );

  const handleCountryCodeChange = useCallback(
    (value: string) => {
      dispatch(setCountryCode(value));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(() => {
    if (!mobileNumber || mobileNumber.length < 10) {
      showToast(t('login.invalidPhoneNumber'), { type: 'error' });
      return;
    }

    sendOTPMutation.mutate(
      { countryCode, mobile: mobileNumber },
      {
        onSuccess: (result) => {
          if (result.registered) {
            // Existing user → navigate to OTP page
            showToast(t('otp.otpSentSuccess', { phoneNumber: `${countryCode}${mobileNumber}` }));
            navigate(ROUTES.otp);
          } else {
            showToast(t('otp.notregistered'));
            // New user → navigate to Signup page
            navigate(ROUTES.signup);
          }
        },
      }
    );
  }, [mobileNumber, countryCode, showToast, navigate, sendOTPMutation, t]);

  const isDev = settings?.ENV === 'dev';
  const versionText = isDev ? `Dev: ${APP_VERSION}` : APP_VERSION;

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Top blue gradient section - 60% height, no border radius (matching Xamarin) */}
      <div
        className="absolute top-0 left-0 right-0 h-[60%]"
        style={{ background: 'linear-gradient(to right, #003366 10%, #0B1B25 100%)' }}
      />

      {/* Bottom white section - 40% height (matching Xamarin WhiteFrameBackgroundColor) */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[40%]"
        style={{ background: 'linear-gradient(to right, #FFFFFF 10%, #FFFFFF 100%)' }}
      />

      {/* Content container */}
      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Header section on blue gradient */}
        <div className="pt-6 px-6 text-left">
          {/* Niagara Logo - reduced gap below */}
          <NiagaraLogo className="mb-2" />

          {/* Welcome text - matches Xamarin text sizes */}
          <h1 className="text-white text-sm md:text-lg font-light mb-0">
            {t('login.welcome', 'Welcome to')}{' '}
            <span className="font-semibold">{t('login.title', 'YMS Niagara')}</span>
          </h1>
          <p className="text-white/80 text-xs md:text-sm">
            {t('login.subtitle', 'Login to Continue')}
          </p>
        </div>

        {/* Floating Login Card - centered on the 60/40 blue-white boundary at any resolution */}
        <div className="absolute top-[60%] left-0 right-0 flex justify-center px-12 md:px-12 lg:px-0 -translate-y-1/2">
          {/* Shadow wrapper - responsive max width */}
          <div className="w-full md:max-w-[80%] lg:max-w-[620px] xl:max-w-[680px]">
            {/* Inner card with shadow and rounded corners (matching Xamarin) */}
            <div
              className="bg-white rounded-[12px] px-5 py-6 md:p-6 md:py-8"
              style={{ boxShadow: '0 4px 20px rgba(128, 128, 128, 0.8)' }}
            >
              {/* Phone Icon - 50px height matching Xamarin HeightRequest="50" */}
              <div className="flex justify-center mb-[10px]">
                <img
                  src="/images/phone.svg"
                  alt=""
                  className="h-[50px] w-[50px]"
                  aria-hidden="true"
                />
              </div>

              {/* Title - 12px font matching Xamarin FontSize="12" */}
              <p className="font-medium text-center text-[#586e80] text-xs mb-[16px] md:mb-[22px]">
                {t('login.mobilenumbertitle')}
              </p>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
                className="space-y-[16px] md:space-y-[22px]"
              >
                {/* VULN-14: Show user-friendly error, never raw API error strings */}
                {sendError && (
                  <div className="rounded-lg bg-red-50 text-red-600 text-xs p-3 text-center">
                    {t('login.failedToSendOTP')}
                  </div>
                )}

                {/* Mobile number input - max 400px width, centered (matching Xamarin WidthRequest="400") */}
                <div className="flex justify-center">
                  <div className="w-full max-w-[400px] space-y-1">
                    <div className="flex min-h-[50px] rounded-lg overflow-hidden border border-gray-300">
                      <CountryCodeSelector
                        value={countryCode}
                        onChange={handleCountryCodeChange}
                        disabled={isSending}
                      />
                      <div className="flex-1 grid rounded-r-lg bg-white focus-within:ring-0">
                        {!mobileNumber && (
                          <span className="col-start-1 row-start-1 self-center px-3 text-[14px] md:text-[16px] text-[#415B6D] font-poppins pointer-events-none">
                            {t('login.enterMobileNumber')}
                          </span>
                        )}
                        <input
                          id="mobile"
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={mobileNumber}
                          onChange={handleMobileChange}
                          autoComplete="tel-national"
                          maxLength={10}
                          disabled={isSending}
                          className="col-start-1 row-start-1 w-full self-center px-3 bg-transparent border-0 outline-none text-gray-700 text-[14px] md:text-[16px]"
                        />
                      </div>
                    </div>
                    {/* Footer note - 10px font matching Xamarin FontSize="10" */}
                    <p className="text-[10px] text-[#1358a1] font-medium">{t('login.warning')}</p>
                  </div>
                </div>

                {/* Submit button - max 300px width, centered, 5px radius matching Xamarin CornerRadius="5" */}
                <div className="flex justify-center">
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    className="w-full max-w-full md:max-w-[300px] h-[50px] md:h-[60px] bg-[#1358a1] hover:bg-[#1358a1] text-[white] text-[12px] font-extralight rounded-[5px]"
                    disabled={isSending}
                  >
                    {t('login.otp')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Version tag + hard reload - right-aligned near bottom of screen */}
        <div
          className={cn(
            'absolute bottom-6 right-6 md:right-[15%] lg:right-[30%] flex items-center gap-2'
          )}
        >
          <div className={cn('rounded px-2 py-1', isDev && 'bg-red-600')}>
            <span className={cn('text-[10px]', isDev ? 'text-white' : 'text-[#445E71]')}>
              {versionText}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              window.location.reload();
            }}
            className="p-2 rounded-full bg-[#1358a1] hover:bg-[#0e4a8a] transition-colors shadow-md"
            aria-label="Reload app"
          >
            <RefreshCw className="h-4 w-4 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main LoginPage Component
// ─────────────────────────────────────────────────────────────────

export function LoginPage() {
  const { t } = useTranslation();
  const configQuery = useBootstrapConfigQuery();
  useCarriersQuery(configQuery.isSuccess);
  usePlantsQuery(configQuery.isSuccess);
  // Derive bootstrap status from TanStack query state
  const isLoading = configQuery.isLoading;
  const isError = configQuery.isError;
  const errorMessage = configQuery.error
    ? configQuery.error instanceof Error
      ? configQuery.error.message
      : t('login.failedToLoadConfig')
    : null;

  // Check maintenance mode from config data
  const isMaintenance = configQuery.data?.settings.APP_MAINTENANCE === true;

  if (isLoading) {
    return <BootstrapLoader />;
  }

  if (isMaintenance) {
    return <MaintenanceMode message={t('login.systemUnderMaintenance')} />;
  }

  if (isError) {
    return (
      <BootstrapError
        error={errorMessage ?? t('login.failedToInitialize')}
        isRetrying={configQuery.isFetching}
      />
    );
  }

  if (configQuery.isSuccess) {
    return <LoginForm />;
  }

  return <BootstrapLoader />;
}
